import React from 'react';
import { UnscheduledEvent } from '../types';
import MovedFromDot from './MovedFromDot';
import { isLockedByOther } from '../services/lockService';
import { getDeferredDueState } from '../utils/deferredAlerts';

/**
 * Props for the DeferredTable component.
 */
interface DeferredTableProps {
  events: UnscheduledEvent[];
  onView: (event: UnscheduledEvent) => void;
  onEdit: (event: UnscheduledEvent) => void;
  /** Map of eventId → alias currently holding that event's edit lock. */
  lockOwners?: Record<string, string>;
  /** The current user's alias, so their own lock never disables their Edit button. */
  currentUserAlias?: string;
}

/**
 * Calculates how many days a deferral has been open: today (UTC) minus the
 * declared date, counted inclusively (the declaration day counts as day 1).
 * This is the deferral's age, independent of the Due Date — items with no Due
 * Date still age. Returns 0 only when the declared date is missing/unparseable.
 */
function getDaysRemaining(timeDeclaredZulu: string, _dueDateZulu: string): number {
  if (!timeDeclaredZulu) return 0;
  const declaredDay = timeDeclaredZulu.trim().split(/[ T]/)[0];
  if (!declaredDay) return 0;
  const declared = new Date(`${declaredDay}T00:00:00Z`);
  if (isNaN(declared.getTime())) return 0;
  const todayDay = new Date().toISOString().split('T')[0];
  const today = new Date(`${todayDay}T00:00:00Z`);
  const diffDays = Math.round((today.getTime() - declared.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays >= 0 ? diffDays + 1 : 0;
}

/**
 * Gets the first defect text for display (defect-log is authoritative).
 */
function getDefectDisplay(event: UnscheduledEvent): string {
  if (event.defects && event.defects.length > 0) {
    return event.defects[0].text;
  }
  return '—';
}

/**
 * Extracts a display-friendly due date from dueDateZulu.
 */
function getDueDateDisplay(event: UnscheduledEvent): string {
  if (!event.dueDateZulu) return '—';
  const parts = event.dueDateZulu.trim().split(' ');
  return parts[0] || '—';
}

/**
 * Renders a single category column of deferred events.
 */
const DeferredColumn: React.FC<{
  title: string;
  events: UnscheduledEvent[];
  onView: (event: UnscheduledEvent) => void;
  onEdit: (event: UnscheduledEvent) => void;
  lockOwners?: Record<string, string>;
  currentUserAlias?: string;
}> = ({ title, events, onView, onEdit, lockOwners, currentUserAlias }) => {
  return (
    <div className="deferred-column">
      <div className="deferred-column-header">
        {title} <span className="deferred-column-count">({events.length})</span>
      </div>
      <div className="deferred-column-body">
        {events.length === 0 && <div className="deferred-column-empty">No events</div>}
        {events.map((event) => {
          const days = getDaysRemaining(event.timeDeclaredZulu, event.dueDateZulu || '');
          const dueState = getDeferredDueState(event);
          return (
            <div key={event.eventId} className="deferred-card">
              <div className="deferred-card-row">
                <span className="deferred-card-aircraft">
                  {event.tailNumber}
                  {event.movedFrom && <MovedFromDot fromRecordType={event.movedFrom} />}
                </span>
                <span className="deferred-card-days">
                  {days} days
                  {event.extension && (
                    <span
                      className="extension-dot"
                      title="Extension granted"
                      aria-label="Extension granted"
                    />
                  )}
                </span>
              </div>
              <div className="deferred-card-defect">{getDefectDisplay(event)}</div>
              <div className="deferred-card-row">
                <span
                  className={`deferred-card-due ${dueState === 'red' ? 'deferred-due-flash-red' : dueState === 'amber' ? 'deferred-due-flash-amber' : ''}`}
                >
                  Due: {getDueDateDisplay(event)}
                </span>
                <div className="deferred-card-actions">
                  <button
                    className="action-btn view-btn"
                    type="button"
                    aria-label="View event"
                    onClick={() => onView(event)}
                  >
                    &#128065;
                  </button>
                  <button
                    className="action-btn edit-btn"
                    type="button"
                    aria-label="Edit event"
                    disabled={isLockedByOther(lockOwners?.[event.eventId], currentUserAlias || '')}
                    title={
                      isLockedByOther(lockOwners?.[event.eventId], currentUserAlias || '')
                        ? `Being edited by ${lockOwners?.[event.eventId]}`
                        : 'Edit event'
                    }
                    onClick={() => onEdit(event)}
                  >
                    &#9998;
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Deferred Events view with 4 vertical columns by category.
 */
const DeferredTable: React.FC<DeferredTableProps> = ({
  events,
  onView,
  onEdit,
  lockOwners,
  currentUserAlias,
}) => {
  // Order events within every column by tail number, ascending.
  const byTailAscending = (a: UnscheduledEvent, b: UnscheduledEvent): number =>
    (a.tailNumber || '').localeCompare(b.tailNumber || '');
  const sorted = [...events].sort(byTailAscending);
  const apuEvents = sorted.filter((e) => e.apuDeferral);
  const etopsEvents = sorted.filter((e) => e.etopsRestricted);
  const catIiiEvents = sorted.filter((e) => e.catIiiRestricted);
  const opsRestrictiveEvents = sorted.filter((e) => e.operationallyRestrictive);

  return (
    <div className="deferred-events-section">
      <div className="deferred-columns">
        <DeferredColumn
          title="APU Deferral Events"
          events={apuEvents}
          onView={onView}
          onEdit={onEdit}
          lockOwners={lockOwners}
          currentUserAlias={currentUserAlias}
        />
        <DeferredColumn
          title="CAT III Restricted Events"
          events={catIiiEvents}
          onView={onView}
          onEdit={onEdit}
          lockOwners={lockOwners}
          currentUserAlias={currentUserAlias}
        />
        <DeferredColumn
          title="ETOPS Restrictive Events"
          events={etopsEvents}
          onView={onView}
          onEdit={onEdit}
          lockOwners={lockOwners}
          currentUserAlias={currentUserAlias}
        />
        <DeferredColumn
          title="Operationally Restrictive Events"
          events={opsRestrictiveEvents}
          onView={onView}
          onEdit={onEdit}
          lockOwners={lockOwners}
          currentUserAlias={currentUserAlias}
        />
      </div>
    </div>
  );
};

export default DeferredTable;
